// src/pages/Home/HomePageVitrine.jsx
import React, { useEffect, useMemo, useState } from "react";
import useScrollToTop from "../../hooks/useScollToTop";
import { useUser } from "./UserContext";
import { saveVisite } from "../../services/visitesServices";
import { getAllHomeSections } from "../../services/homeSectionsServices";

/* Sections existantes */
import AuthIcons from "./AuthIcons";
import ScrollIndicator from "./ScrollIndicator";
import HeaderSlider from "./HeaderSlider";
import InfoClub from "./InfoClub";
import About from "./About";
import Diaporama from "./Diaporama";
import Disciplines from "./Disciplines/Disciplines";
import GroupTable from "./GroupTable";
import PlanningCours from "./PlanningCours";
import NosEquipes from "./NosEquipes";
import HeaderCount from "./HeaderCount";
import UpcomingEvents from "./UpcomingEvents";
import LastNews from "./LastNews";
import LastPalmares from "./LastPalmares";
import LastGallerie from "./LastGallerie";
import BeltCounts from "./BeltCounts";
import PartenairesCarousel from "./PartenairesCarousel";
import FormContact from "./FormContact";

/* Helpers */
const isImageUrl = (url = "") =>
  /\.(png|jpe?g|webp|gif|avif|bmp|svg)(\?.*)?$/i.test(url) ||
  url.includes("res.cloudinary.com");

const getYouTubeId = (url = "") => {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return u.pathname.slice(1);
    if (u.hostname.includes("youtube.com")) return u.searchParams.get("v");
  } catch (_) {}
  return null;
};

// ⬇️ remplace ton getYouTubeId par ceci
const getYouTubeEmbedSrc = (url = "") => {
  if (!url) return null;
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "");

    // 1) Lien youtu.be/ID
    if (host === "youtu.be") {
      const id = u.pathname.slice(1);
      if (id) return `https://www.youtube.com/embed/${id}`;
    }

    // 2) Lien youtube.com/...
    if (host.endsWith("youtube.com")) {
      // Déjà un /embed/ → on laisse tel quel
      if (u.pathname.startsWith("/embed/")) {
        return `https://www.youtube.com${u.pathname}${u.search}`;
      }

      // /watch?v=ID
      if (u.pathname === "/watch") {
        const id = u.searchParams.get("v");
        if (id) {
          const start = u.searchParams.get("t") || u.searchParams.get("start");
          const params = new URLSearchParams();
          if (start) {
            // accepte "90" ou "90s"
            const s = /s$/i.test(start)
              ? parseInt(start, 10)
              : parseInt(start, 10);
            if (Number.isFinite(s)) params.set("start", s);
          }
          const qs = params.toString();
          return `https://www.youtube.com/embed/${id}${qs ? `?${qs}` : ""}`;
        }
      }

      // /shorts/ID
      if (u.pathname.startsWith("/shorts/")) {
        const id = u.pathname.split("/")[2];
        if (id) return `https://www.youtube.com/embed/${id}`;
      }

      // /live/ID
      if (u.pathname.startsWith("/live/")) {
        const id = u.pathname.split("/")[2];
        if (id) return `https://www.youtube.com/embed/${id}`;
      }
    }

    // 3) En dernier recours : si l’URL contient déjà /embed/, on l’utilise tel quel
    if (url.includes("/embed/")) return url;

    return null;
  } catch {
    return null;
  }
};

// ⬇️ remplace entièrement ce composant
const PhotoFull = ({ mediaURL, description, alt = "Photo" }) => {
  if (!mediaURL || !isImageUrl(mediaURL)) return null;

  return (
    <figure className="w-full my-8">
      <img
        src={mediaURL}
        alt={description || alt}
        loading="lazy"
        className="block w-full h-auto select-none"
        /* 
          - w-full  : prend toute la largeur disponible
          - h-auto  : garde le ratio, pas de rognage
          - (option) si tu veux limiter l’immense hauteur d’images très panoramiques :
          style={{ maxHeight: "90vh", objectFit: "contain" }}
        */
      />
      {/* Si tu veux un bandeau de description, décommente : */}
      {/* {description && (
        <figcaption className="mt-2 text-sm text-slate-600 dark:text-slate-300 text-center">
          {description}
        </figcaption>
      )} */}
    </figure>
  );
};

// ⬇️ remplace ton composant VideoEmbed par ceci
const VideoEmbed = ({ mediaURL, title = "Vidéo" }) => {
  const src = getYouTubeEmbedSrc(mediaURL || "");
  if (!src) return null;
  return (
    <div className="my-8 flex justify-center px-4 bg-lightBackground dark:bg-darkBackground">
      <div className="w-full max-w-[1280px] aspect-video">
        <iframe
          className="w-full h-full rounded-lg shadow-md"
          src={src}
          title={title}
          frameBorder="0"
          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        />
      </div>
    </div>
  );
};

function HomePageVitrine() {
  useScrollToTop();
  const { user } = useUser();
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);

  // Enregistre une visite / jour
  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    const lastVisitDate = localStorage.getItem("lastVisitDate");
    if (lastVisitDate !== today) {
      saveVisite();
      localStorage.setItem("lastVisitDate", today);
    }
  }, []);

  // Charge l’ordre des sections actives
  useEffect(() => {
    (async () => {
      try {
        const data = await getAllHomeSections(true); // seulement is_active = true
        const sorted = Array.isArray(data)
          ? [...data].sort((a, b) => a.position - b.position)
          : [];
        setSections(sorted);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Dictionnaire: slug -> rendu
  const registry = useMemo(
    () => ({
      header: () => <HeaderSlider />,
      infoclub: () => <InfoClub />,
      about: () => <About />,
      carrousel: () => <Diaporama />, // adapte si tu as un composant dédié "Carrousel"
      disciplines: () => <Disciplines />,
      infogroupes: () => <GroupTable />,
      // planning: () => <PlanningCours />,
      team: () => <NosEquipes />,
      stats: () => <HeaderCount />,
      events: () => <UpcomingEvents />,
      news: () => <LastNews />,
      palmares: () => <LastPalmares />,
      albums: () => <LastGallerie />,
      belt: () => <BeltCounts />,
      partenaires: () => <PartenairesCarousel />,
      contact: () => <FormContact />,

      // Spéciaux: contenus médias
      photo01: (s) => (
        <PhotoFull
          mediaURL={s.mediaURL}
          description={s.description}
          alt="Photo 01"
        />
      ),
      photo02: (s) => (
        <PhotoFull
          mediaURL={s.mediaURL}
          description={s.description}
          alt="Photo 02"
        />
      ),
      video01: (s) => (
        <VideoEmbed
          mediaURL={s.mediaURL}
          title={s.section_name || "Vidéo 01"}
        />
      ),
    }),
    []
  );

  return (
    <div>
      {!user && <AuthIcons />}
      <ScrollIndicator />

      {/* Offset navbar */}
      <div className="pt-[130px]" aria-hidden />

      {/* Loading skeleton simple */}
      {loading && (
        <div className="mx-auto max-w-7xl px-4 py-6 space-y-4">
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="h-40 rounded-2xl bg-slate-100 dark:bg-slate-800 animate-pulse"
            />
          ))}
        </div>
      )}

      {!loading &&
        sections.map((s) => {
          const render = registry[s.slug];
          if (!render) return null; // slug inconnu -> ignore
          return (
            <section id={s.slug} key={s.id} className="scroll-mt-24">
              {render(s)}
            </section>
          );
        })}
    </div>
  );
}

export default HomePageVitrine;
